import { useCallback } from 'react';
import { uniq } from 'lodash';
import { useGetAccountLevelMetadataEnabledQuery } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useIsUploadLinksEnabled } from '@frameio/shared/upload-links/hooks';
import { useAssetForOperationCheckLazyQuery } from '../gql/generated/AssetForOperationCheck';
import type { AssetParentFragment } from '../gql/generated/AssetParent';

export type AssetCheckInput = {
  assetIds: string[];
  destination: AssetParentFragment;
};

export type ShareRiskCheckResult = {
  hasRestrictedFiles: boolean;
  hasRestrictedFolders: boolean;
  hasUploadLinkFolders: boolean;
};

export type MoveCopyCheckResult = {
  shouldShowWarning: boolean;
  isAnyRestricted: boolean;
  isCrossProject: boolean;
};

export type UseAssetOperationCheckReturn = {
  checkShareRisks: (assetIds: string[]) => Promise<ShareRiskCheckResult>;
  checkMoveCopyPreconditions: (
    input: AssetCheckInput
  ) => Promise<MoveCopyCheckResult>;
  checkMoveCopyPreconditionsForMultipleSources: (
    input: AssetCheckInput
  ) => Promise<MoveCopyCheckResult>;
};

/**
 * Hook providing functions to check asset conditions before operations (move/copy/share).
 *
 * Exposes multiple callbacks for different checks, but uses a single underlying GraphQL query.
 * Relies on Apollo Client caching to optimize potentially redundant calls.
 *
 * @returns An object containing check functions: `checkShareRisks` and `checkMoveCopyPreconditions`.
 */
export const useAssetOperationCheck = (): UseAssetOperationCheckReturn => {
  const [AssetForOperationCheck] = useAssetForOperationCheckLazyQuery();

  const isUploadLinksEnabled = useIsUploadLinksEnabled();

  const accountId = useActiveAccountId();
  const isAccountLevelMetadataEnabled = useGetAccountLevelMetadataEnabledQuery({
    variables: {
      accountId,
    },
    skip: !accountId,
  })?.data?.account?.useAccountScopedFieldDefinitions;

  // Account.assets requires an account id; without one, skip the request.
  const runQuery = useCallback(
    async (assetIds: string[]) => {
      if (!accountId) {
        return { data: undefined };
      }

      return AssetForOperationCheck({
        variables: { accountId, assetIds, first: assetIds.length },
      });
    },
    [AssetForOperationCheck, accountId]
  );

  const checkShareRisks = useCallback(
    async (assetIds: string[]): Promise<ShareRiskCheckResult> => {
      const { data } = await runQuery(assetIds);

      let hasRestrictedFiles = false;
      let hasRestrictedFolders = false;
      let hasUploadLinkFolders = false;

      data?.account?.assets?.nodes.forEach((asset) => {
        if (asset.__typename === 'FolderAsset') {
          if (
            isUploadLinksEnabled &&
            (asset.hasActiveUploadLinks || asset.hasActiveUploadLinksInSubtree)
          ) {
            hasUploadLinkFolders = true;
          }

          if (asset.restricted || asset.isRestrictedDescendant) {
            hasRestrictedFolders = true;
            return;
          }
        }

        if (!asset.parent) return;

        const folderParent =
          asset.parent.__typename === 'VersionStackAsset'
            ? asset.parent.parent
            : asset.parent;

        if (
          folderParent?.__typename === 'FolderAsset' &&
          (folderParent?.restricted || folderParent?.isRestrictedDescendant)
        ) {
          hasRestrictedFiles = true;
        }
      });

      return {
        hasRestrictedFiles,
        hasRestrictedFolders,
        hasUploadLinkFolders,
      };
    },
    [isUploadLinksEnabled, runQuery]
  );

  const checkMoveCopyPreconditions = useCallback(
    async (input: AssetCheckInput): Promise<MoveCopyCheckResult> => {
      const { assetIds, destination } = input;
      const { data } = await runQuery(assetIds);

      const isAnyRestricted =
        data?.account?.assets?.nodes.some((asset) => {
          if (asset.__typename === 'FolderAsset') {
            return asset.restricted === true;
          }
          return false;
        }) ?? false;

      const sourceProjectId = data?.account?.assets?.nodes[0]?.project?.id;
      const targetProjectId = destination.project?.id;

      const isCrossProject = isAccountLevelMetadataEnabled
        ? false
        : !!(
            sourceProjectId &&
            targetProjectId &&
            sourceProjectId !== targetProjectId
          );

      const shouldShowWarning = isAnyRestricted || isCrossProject;

      return {
        shouldShowWarning,
        isAnyRestricted,
        isCrossProject,
      };
    },
    [runQuery, isAccountLevelMetadataEnabled]
  );

  const checkMoveCopyPreconditionsForMultipleSources = useCallback(
    async ({
      assetIds,
      destination,
    }: AssetCheckInput): Promise<MoveCopyCheckResult> => {
      const { data } = await runQuery(assetIds);

      const isAnyRestricted =
        data?.account?.assets?.nodes.some((asset) => {
          if (asset.__typename === 'FolderAsset') {
            return asset.restricted === true;
          }
          return false;
        }) ?? false;

      const sourceProjectIds = data?.account?.assets?.nodes.map(
        (asset) => asset.project?.id
      );
      const isSameSource = Boolean(
        sourceProjectIds && uniq(sourceProjectIds).length === 1
      );

      const targetProjectId = destination.project?.id;
      const isCrossProject =
        !isSameSource ||
        (Boolean(targetProjectId) && sourceProjectIds?.[0] !== targetProjectId);

      const shouldShowWarning = isAnyRestricted || isCrossProject;

      return {
        shouldShowWarning,
        isAnyRestricted,
        isCrossProject,
      };
    },
    [runQuery]
  );

  return {
    checkShareRisks,
    checkMoveCopyPreconditions,
    checkMoveCopyPreconditionsForMultipleSources,
  };
};
